import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MarkdownDocument } from '@evi/ui';
import { useAuthStore } from '@/store/auth';
import { fetchTermsStatus, acceptTerms, logout, markTermsSeen, type TermsDocType, type TermsStatus } from '@/lib/api';
import { markExplicitSignOut } from '@/lib/sessionRestore';
import { termsDocOpener, termsTickKey, termsTickSentence } from '@/lib/termsTick';
import styles from './TermsGate.module.css';

/**
 * Blocking terms-acceptance gate for the admin app. The server decides which documents apply (driver_tos always,
 * payout_terms once the user is an earner, sp_terms for Sales Partners, site_host_terms for a site's Building Admin or
 * Property Manager) so the two apps cannot drift apart, and which of them need accepting now:
 *
 *   - at first sign-in, every applicable document, on one screen;
 *   - after a MATERIAL change, only the changed documents, with what changed;
 *   - a NON-MATERIAL change never gates: TermsUpdateBanner shows it until closed, and it is emailed.
 *
 * Warren, 21 October 2026, evening: "one tick naming the three documents, each title a link that opens it, then Accept.
 * Keep recording acceptance of each document and version separately, as now." This replaces the morning's screen (one
 * checkbox per document, each read to the end first): one real checkbox whose sentence names every listed document, each
 * title opening it (the rendered text in a dialog, or its hosted page in a new tab), then Accept. Accept still records
 * each document and version on its own, one request per document in the server's order, each audited as before; the Api
 * never asked for reading to the end, so nothing there changed. A new list clears the tick.
 *
 * A failed status request blocks rather than lets an admin through (a gate that opens on a failed request can be
 * opened on purpose). Mounted once in AppShell, so no admin surface renders outside it.
 */
const APPLICABLE_QUERY_KEY = ['terms', 'status', 'applicable'] as const;

/** One document, opened from its title: the text the server sent, in a dialog over the screen. */
function DocReader({ doc, onClose }: { doc: TermsStatus; onClose: () => void }) {
  const close = useRef<HTMLButtonElement | null>(null);
  // Focus moves into the dialog, and back to the title that opened it.
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    close.current?.focus();
    return () => back?.focus?.();
  }, []);
  return <div className={styles.readerScrim} role="dialog" aria-modal aria-labelledby="evi-terms-reader-title"
    onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
    <div className={styles.reader}>
      <div className={styles.readerHead}>
        <h3 id="evi-terms-reader-title" className={styles.docTitle}>{doc.label} <span className={styles.version}>Version {doc.version}</span></h3>
        <button type="button" ref={close} className={styles.readerClose} aria-label={`Close the ${doc.label}`} onClick={onClose}><X size={18} /></button>
      </div>
      {doc.state === 'changed' && doc.change_summary && <p className={styles.change}>What changed: {doc.change_summary}</p>}
      <div className={styles.body} tabIndex={0} role="region" aria-label={doc.label}><MarkdownDocument text={doc.body ?? ''} /></div>
      <div className={styles.readerActions}><button type="button" className={styles.primary} onClick={onClose}>Done</button></div>
    </div>
  </div>;
}

/** A title in the tick's sentence: a link that opens the document. Inside the label, a tap on it opens the document and
 * leaves the box as it was. */
function DocTitle({ doc, onOpen }: { doc: TermsStatus; onOpen: (d: TermsStatus) => void }) {
  const how = termsDocOpener(doc);
  if (how === 'dialog') return <button type="button" className={styles.docLink} onClick={() => onOpen(doc)}>{doc.label}</button>;
  if (how === 'tab') return <a className={styles.docLink} href={doc.hosted_url ?? undefined} target="_blank" rel="noopener noreferrer">{doc.label}</a>;
  return <>{doc.label}</>;
}

export function TermsGate({ docs, onAccepted }: { docs?: TermsDocType[]; onAccepted?: () => void } = {}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [...APPLICABLE_QUERY_KEY, ...(docs ?? [])], queryFn: () => fetchTermsStatus(docs), enabled: isAuthenticated, staleTime: 5 * 60_000 });
  const pending = (q.data ?? []).filter(d => !d.accepted);
  const [ticked, setTicked] = useState(false);
  const [reading, setReading] = useState<TermsStatus | null>(null);
  const key = termsTickKey(pending);
  useEffect(() => { setTicked(false); setReading(null); }, [key]);
  // The one tick accepts every listed document, and each document and version is still its own record (and its own
  // audit), in the server's order.
  const accept = useMutation({
    mutationFn: async (list: TermsStatus[]) => { for (const d of list) await acceptTerms(d.doc_type); },
    onSuccess: () => onAccepted?.(),
    onSettled: () => qc.invalidateQueries({ queryKey: APPLICABLE_QUERY_KEY }),
  });
  const onSignOut = useCallback(async () => {
    // Marked here too, not only inside logout(): this path reloads, and the
    // startup restore must not bring the session straight back.
    markExplicitSignOut();
    try { await logout(); } catch { /* ignore */ }
    clearAuth();
    window.location.assign('/login');
  }, [clearAuth]);

  const blockedUnresolved = Boolean(isAuthenticated && (q.isError || (q.isSuccess && !q.data) || pending.some(d => d.unavailable)));
  if (!isAuthenticated || typeof document === 'undefined') return null;
  if (!blockedUnresolved && pending.length === 0) return null;

  if (blockedUnresolved) {
    return createPortal(
      <div className={styles.overlay} role="dialog" aria-modal aria-labelledby="evi-terms-title">
        <div className={styles.card}>
          <p className={styles.eyebrow}>We cannot continue yet</p>
          <h2 className={styles.title} id="evi-terms-title">Terms could not be loaded</h2>
          <p className={styles.unavailableBody}>We could not load the terms you need to accept, so we cannot let you continue. This is usually a connection problem. Try again in a moment.</p>
          <div className={styles.actions}>
            <button type="button" className={styles.ghost} onClick={onSignOut}>Sign out</button>
            <button type="button" className={styles.primary} onClick={() => void q.refetch()} disabled={q.isFetching}>{q.isFetching ? 'Trying…' : 'Try again'}</button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  const changed = pending.some(d => d.state === 'changed');
  return createPortal(
    <div className={styles.overlay} role="dialog" aria-modal aria-labelledby="evi-terms-title">
      <div className={`${styles.card} ${styles.combined}`}>
        <p className={styles.eyebrow}>{changed ? 'We updated terms that apply to you' : 'Before you start'}</p>
        <h2 id="evi-terms-title" className={styles.title}>{changed ? 'Review the changes' : pending.length === 1 ? 'Accept your terms' : `Accept your ${pending.length} documents`}</h2>
        <p className={styles.unavailableBody}>{changed ? 'Only the documents that changed are listed.' : 'These apply to how you use EV Initiative.'} Tap a title to read it.</p>
        {pending.filter(d => d.state === 'changed' && d.change_summary).map(d =>
          <p key={`${d.doc_type}:${d.version}`} className={styles.change}>What changed in the {d.label}: {d.change_summary}</p>)}
        {/* A real checkbox (agreeing to terms), never the shared on/off switch. */}
        <label className={styles.checkboxRow}>
          <input type="checkbox" checked={ticked} onChange={e => setTicked(e.target.checked)} disabled={accept.isPending} />
          <span>{termsTickSentence(pending).map((p, i) => (typeof p === 'string' ? p : <DocTitle key={`${p.doc_type}:${i}`} doc={p} onOpen={setReading} />))}</span>
        </label>
        {accept.isError && <div className={styles.error} role="alert">Could not record your acceptance. Check your connection and try again.</div>}
        <div className={styles.actions}>
          <button type="button" className={styles.ghost} onClick={onSignOut} disabled={accept.isPending}>Sign out</button>
          <button type="button" className={styles.primary} onClick={() => accept.mutate(pending)} disabled={!ticked || accept.isPending}>
            {accept.isPending ? <><Loader2 size={14} className={styles.spin} /> Saving…</> : 'Accept'}
          </button>
        </div>
      </div>
      {reading && <DocReader doc={reading} onClose={() => setReading(null)} />}
    </div>,
    document.body,
  );
}

/** A non-material terms update: a banner, never a gate, until the person closes it (Warren, 21 October 2026). */
export function TermsUpdateBanner() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [...APPLICABLE_QUERY_KEY], queryFn: () => fetchTermsStatus(), enabled: isAuthenticated, staleTime: 5 * 60_000 });
  const updates = (q.data ?? []).filter(d => d.accepted && d.update);
  if (!isAuthenticated || !updates.length) return null;
  return <div className={styles.updateBanner} role="status">
    {updates.map(d => <div key={`${d.doc_type}:${d.update!.version}`} className={styles.updateRow}>
      <span>We updated the {d.label} (version {d.update!.version}).{d.update!.change_summary ? ` ${d.update!.change_summary}` : ''} Nothing changes in what you need to do.
        {d.hosted_url && <> <a href={d.hosted_url} target="_blank" rel="noopener noreferrer">Read it</a></>}</span>
      <button type="button" aria-label={`Close the ${d.label} update`} className={styles.updateClose}
        onClick={() => void markTermsSeen(d.doc_type, d.update!.version).then(() => qc.invalidateQueries({ queryKey: APPLICABLE_QUERY_KEY }))}><X size={16} /></button>
    </div>)}
  </div>;
}
